<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Error state -->
        <div v-if="error" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
            <Button variant="danger" @click="refresh">
                {{ $t('back.dashboard.retry') }}
            </Button>
        </div>

        <!-- Loading skeleton -->
        <template v-if="loading">
            <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-3 tw:gap-6">
                <div v-for="i in 3" :key="i" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <div class="tw:h-64 tw:bg-gray-100 tw:animate-pulse tw:rounded"></div>
                </div>
            </div>
        </template>

        <!-- Data -->
        <template v-else-if="data">
            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.sales.section_title') }}</h3>
            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.sales.section_description') }}</p>

            <!-- 3 charts in a grid -->
            <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-3 tw:gap-6 tw:mb-8">
                <!-- Online vs Taquilla donut -->
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.sales.online_taquilla_title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.sales.online_taquilla_desc') }}</p>
                    <Chart
                        v-if="onlineDonutData.length > 1"
                        :type="onlineChartType"
                        :data="onlineDonutData"
                        :options="donutChartOptions"
                        :height="260"
                    />
                    <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                        {{ $t('back.dashboard.no_data') }}
                    </div>
                </div>

                <!-- Digital evolution line -->
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.sales.digital_evolution_title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.sales.digital_evolution_desc') }}</p>
                    <Chart
                        v-if="digitalLineData.length > 1"
                        type="LineChart"
                        :data="digitalLineData"
                        :options="lineChartOptions"
                        :height="260"
                    />
                    <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                        {{ $t('back.dashboard.no_data') }}
                    </div>
                </div>

                <!-- Anticipadas vs Último momento donut -->
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.sales.anticipadas_title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-4">Ventas anticipadas vs último momento (últimas {{ thresholdHours }}h)</p>
                    <Chart
                        v-if="anticipadasDonutData.length > 1"
                        :type="anticipadasChartType"
                        :data="anticipadasDonutData"
                        :options="donutChartOptions"
                        :height="260"
                    />
                    <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                        {{ $t('back.dashboard.no_data') }}
                    </div>
                </div>
            </div>

            <!-- Multi-season breakdown table -->
            <div v-if="isMultiSeason && seasonBreakdown?.length" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white">
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.breakdown_title') }}</h4>
                <table class="tw:w-full tw:text-sm">
                    <thead>
                        <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                            <th class="tw:pb-2">{{ $t('back.dashboard.season') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.sales.kpi_online') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.sales.kpi_taquilla') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.sales.anticipadas_title') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in seasonBreakdown" :key="row.season" class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-1.5">{{ row.season }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatCurrency(row.onlineTotal) }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatCurrency(row.taquillaTotal) }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatCurrency(row.anticipadasTotal) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- Empty state -->
        <div v-else class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.no_data') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { toRef } from 'vue';
    import { useSales } from '@/composables/admin/dashboard/useSales';
    import { formatCurrency } from '@/utils/format';
    import Chart from '@/modules/admin/ui/Chart.vue';
    import Button from '@/modules/admin/ui/Button.vue';

    const props = defineProps({
        seasonIds: { type: Array, required: true },
        seasons: { type: Array, required: true },
    });


    const seasonIdsRef = toRef(props, 'seasonIds');
    const seasonsRef = toRef(props, 'seasons');

    const {
        data,
        loading,
        error,
        isMultiSeason,
        onlineDonutData,
        digitalLineData,
        anticipadasDonutData,
        seasonBreakdown,
        donutChartOptions,
        lineChartOptions,
        onlineChartType,
        anticipadasChartType,
        thresholdHours,
        refresh,
    } = useSales(seasonIdsRef, seasonsRef);
</script>
